import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, MapPin } from "lucide-react";
import Seo from "@/components/Seo";

const RELATED_SERVICES = [
  ["Event Photography", "/services/event-photography-new-orleans"],
  ["Foto Booth", "/services/foto-booth-new-orleans"],
  ["Real Estate Media", "/services/real-estate-media-new-orleans"],
  ["Food + Restaurant", "/services/restaurant-food-photography-new-orleans"],
  ["Airbnb + Hospitality", "/services/hospitality-airbnb-media-new-orleans"],
  ["Business Content", "/services/business-content-new-orleans"],
  ["Event Video", "/services/event-video-new-orleans"],
  ["Instant Photo Printing", "/services/instant-photo-printing-new-orleans"],
  ["Open Bar for Photos", "/services/open-bar-for-photos"],
];

export default function ServicePage({ title, eyebrow, description, path, h1, intro, bullets, serviceType }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: serviceType,
    provider: { "@type": "Organization", "@id": "https://geauxfotos.com/#organization", name: "Fotos on the Geaux", url: "https://geauxfotos.com/" },
    areaServed: [
      { "@type": "City", name: "New Orleans" },
      { "@type": "City", name: "Metairie" },
    ],
    url: `https://geauxfotos.com${path}`,
    description,
  };
  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://geauxfotos.com/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://geauxfotos.com/services" },
      { "@type": "ListItem", position: 3, name: serviceType, item: `https://geauxfotos.com${path}` },
    ],
  };

  return (
    <div className="service-page min-h-screen bg-[#090909] text-[#F5F0E8]">
      <Seo title={title} description={description} path={path} imageAlt={`${serviceType} by Fotos on the Geaux in New Orleans`} schema={[schema, breadcrumbs]} />
      <main className="mx-auto max-w-5xl px-5 py-16 md:py-24">
        <div className="flex items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white"><ArrowLeft size={15} /> Fotos on the Geaux</Link>
          <Link to="/book" className="inline-flex items-center gap-2 rounded-full bg-[#F5F0E8] px-5 py-2.5 font-accent text-[10px] tracking-[.18em] text-black"><CalendarDays size={14} /> BOOK FOTOS</Link>
        </div>
        <p className="mt-16 max-w-full font-accent text-[11px] tracking-[.24em] text-[#39E75F] sm:text-xs sm:tracking-[.35em]">{eyebrow}</p>
        <h1 className="mt-5 max-w-4xl font-display text-6xl font-black leading-[.9] tracking-tighter md:text-8xl">{h1}</h1>
        <p className="mt-8 max-w-3xl text-xl leading-relaxed text-white/72">{intro}</p>
        <div className="mt-10 flex items-center gap-2 text-sm text-white/50"><MapPin size={16} /> New Orleans · Metairie · Greater New Orleans</div>

        <section className="mt-16" aria-labelledby="service-inclusions">
          <p className="font-accent text-[10px] tracking-[.28em] text-[#39E75F]">WHAT'S INCLUDED</p>
          <h2 id="service-inclusions" className="mt-3 max-w-3xl font-display text-4xl font-black tracking-tighter md:text-5xl">{serviceType} in New Orleans.</h2>
          <p className="mt-4 max-w-3xl text-base leading-7 text-white/55">Built for New Orleans-area clients who need polished visuals, a clear production process and deliverables they can actually use.</p>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {bullets.map((item) => (
              <div key={item} className="rounded-2xl border border-white/10 bg-white/[.035] p-6 text-lg leading-relaxed text-white/72">{item}</div>
            ))}
          </div>
        </section>

        <section className="mt-20 grid gap-5 md:grid-cols-3" aria-labelledby="service-faq">
          <div className="md:col-span-3">
            <p className="font-accent text-[10px] tracking-[.28em] text-[#39E75F]">GOOD TO KNOW</p>
            <h2 id="service-faq" className="mt-3 font-display text-4xl font-black tracking-tighter md:text-5xl">Planning your shoot.</h2>
          </div>
          <article className="rounded-2xl border border-white/10 bg-white/[.025] p-6">
            <h3 className="font-display text-2xl font-bold">Where do you work?</h3>
            <p className="mt-3 text-sm leading-6 text-white/55">New Orleans, Metairie and the Greater New Orleans area, with travel-ready bookings available for the right project.</p>
          </article>
          <article className="rounded-2xl border border-white/10 bg-white/[.025] p-6">
            <h3 className="font-display text-2xl font-bold">How do I book?</h3>
            <p className="mt-3 text-sm leading-6 text-white/55">Send your preferred date, location and project details. Availability and final scope are confirmed before the booking is locked in.</p>
          </article>
          <article className="rounded-2xl border border-white/10 bg-white/[.025] p-6">
            <h3 className="font-display text-2xl font-bold">Can services be combined?</h3>
            <p className="mt-3 text-sm leading-6 text-white/55">Yes. Photo, video, Foto Booth, instant-print and recurring-content options can be combined when they fit the project.</p>
          </article>
        </section>

        <section className="mt-20 border-y border-white/10 py-10" aria-labelledby="related-services">
          <p className="font-accent text-[10px] tracking-[.28em] text-[#39E75F]">KEEP EXPLORING</p>
          <h2 id="related-services" className="mt-3 font-display text-3xl font-black tracking-tighter md:text-4xl">Related Fotos on the Geaux services.</h2>
          <div className="mt-6 flex flex-wrap gap-2">
            {RELATED_SERVICES.filter(([, href]) => href !== path).map(([label, href]) => (
              <Link key={href} to={href} className="rounded-full border border-white/15 px-4 py-2 text-xs text-white/65 transition hover:border-white/40 hover:text-white">
                {label}
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-20 rounded-3xl border border-white/10 bg-white/[.03] p-8 md:p-12">
          <p className="font-accent text-xs tracking-[.3em] text-[#E32636]">FOTOS ON THE GEAUX</p>
          <h2 className="mt-4 font-display text-4xl font-black tracking-tighter md:text-6xl">Ready to put your date on the books?</h2>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/65">Choose your service, date, time and venue. I’ll review availability and follow up with next steps to confirm the booking.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/book" className="inline-flex items-center gap-2 rounded-full bg-[#F5F0E8] px-6 py-3 font-accent text-sm tracking-widest text-black">BOOK FOTOS <ArrowRight size={16} /></Link>
            <Link to="/pole-and-polaroids" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 font-accent text-sm tracking-widest">SEE A LIVE EXPERIENCE <ArrowRight size={16} /></Link>
          </div>
        </section>
      </main>
    </div>
  );
}

